<template>
  <li class="toc-item"  @click.stop.prevent="select(itemPath)">
    <span v-if="isWidget"
          :id="'toc-item-' + flatListIdx"
          :class="{ 'is-active': isWidget && (itemPath === widgetContentPath),
                    'toc-menu-list': isWidget }"
          :href="'/ui' + itemPath">
      <toc-item :item="item"
                :highlighted="highlighted"
                :highlighted-name-html="getHighlightedNameHtml(item)"
                :help-text-html="getHighlightedHelpTextHtml(item)"
                :help-text="getHighlightedHelpText(item)"
                :open="open"
                @toggle="toggle"/>
    </span>
    <router-link  v-else
                  :to="{ path: itemPath }"
                  :id="item.type + ':' + item.id"
                  exact>
      <toc-item :item="item"
                :highlighted="highlighted"
                :highlighted-name-html="getHighlightedNameHtml(item)"
                :help-text-html="getHighlightedHelpTextHtml(item)"
                :help-text="getHighlightedHelpText(item)"
                :open="open"
                @toggle="toggle"/>
    </router-link>
    <template v-if="item.children">
      <ul v-show="open"
          v-for="(child, childIdx) in item.children"
          :key="child.id"
          class="menu-list"
          :class="{'menu-list-highlighted': isHighlighted(child)}">
        <table-of-contents-item :item="child"
                                :item-path="itemPath + '/' + child.type + ':' + child.id"
                                :highlighted="highlighted"
                                :force-open="forceOpen"
                                :is-widget="isWidget"
                                :flat-list-idx="flatListIdx + childIdx"
                                :widget-content-path="widgetContentPath"
                                @click.stop.prevent="select"
                                @select="$emit('select', $event)"
                                @widgetNav="$emit('widgetNav', $event)"/>
      </ul>
    </template>
  </li>
</template>

<script>
import TocItem from './TocItem.vue'
import i18n from '@/locales/index.js'

export default {
  name: 'TableOfContentsItem',
  components: { TocItem },
  props: {
    item: {
      type: Object,
      default: () => {}
    },
    itemPath: {
      type: String,
      default: ''
    },
    highlighted: {
      type: Array,
      default: () => []
    },
    forceOpen: {
      type: Number,
      default: 0
    },
    isWidget: Boolean,
    flatListIdx: {
      type: Number,
      required: true
    },
    widgetContentPath: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      open: true
    };
  },
  watch: {
    forceOpen(newValue, oldValue) {
      this.open = newValue > oldValue;
    }
  },
  computed: {
    tocQuery () {
      const q = this.$store.state.search?.filters?.tocQuery
      return (typeof q === 'string') ? q : ''
    }
  },
  methods: {
    getLocaleString () {
      const rawLocale = i18n?.global?.locale
      return (rawLocale && typeof rawLocale === 'object' && 'value' in rawLocale)
        ? rawLocale.value
        : rawLocale
    },
    stripHtml (html) {
      if (html == null) return ''
      return String(html).replace(/<[^>]*>/g, '')
    },
    getHighlightedFields (match) {
      const highlightedFields = match?.highlightedFields
      if (!highlightedFields) return null
      if (Array.isArray(highlightedFields)) {
        return highlightedFields.reduce((acc, entry) => {
          if (!entry) return acc
          return { ...acc, ...entry }
        }, {})
      }
      return highlightedFields
    },
    getHighlightedNameHtml (item) {
      const match = this.getItemMatch(item)
      const locale = this.getLocaleString()

      const key = `name_${String(locale || '').replace('-', '_')}`
      const highlightedFields = this.getHighlightedFields(match)
      const html = highlightedFields?.[key]?.[0]
      return html || null
    },
    getHighlightedHelpText (item) {
      const match = this.getItemMatch(item)

      const highlightedFields = this.getHighlightedFields(match)
      if (!highlightedFields) return ''
      const keys = Object.keys(highlightedFields)
      const filteredKeys = keys?.filter(k => !k.startsWith("name_"))
      if (filteredKeys.length > 1) {
        return i18n.global.tc(
          'searchMatchHelp.fields',
          filteredKeys.length,
          { count: filteredKeys.length }
        )
      }
      //try page filename
      const idMatch = highlightedFields?.['identifier']?.[0]
      if (idMatch) {
        return i18n.global.t('searchMatchHelp.pageFilename', {
          filename: this.stripHtml(idMatch)
        })
      }
      //next try a page tag
      const tagValueMatch = this.getHighlightedTag(match)
      if (tagValueMatch) return this.stripHtml(tagValueMatch)
      //part match
      const partKey = keys.find(k => k.startsWith("part_"))
      if (partKey) {
        return i18n.global.t('searchMatchHelp.part')
      }
      //try document text
      const textMatch = highlightedFields['text']
      if (textMatch) {
        return i18n.global.t('searchMatchHelp.documentText')
      }
      return ''
    },
    getHighlightedHelpTextHtml (item) {
      const match = this.getItemMatch(item)
      const locale = this.getLocaleString()

      const highlightedFields = this.getHighlightedFields(match)
      if (!highlightedFields) return null

      const keys = Object.keys(highlightedFields)
      const filteredKeys = keys?.filter(k => !k.startsWith("name_"))
      if ((filteredKeys?.length ?? 0) > 1) {
        return null
      }
      //description gets to use the highlight html
      const key = `description_${String(locale || '').replace('-', '_')}`
      const htmlDescription = highlightedFields?.[key]?.[0]
      if (htmlDescription) return htmlDescription

      return null
    },
    isHighlighted(item) {
      //normalize the type and id so type errors are avoided
      const normalizeId = (v) => (v == null ? '' : String(v))
      const normalizeType = (v) => (v == null ? '' : String(v).trim().toLowerCase())

      const itemId = normalizeId(item?.id)
      const itemType = normalizeType(item?.type)

      return (this.highlighted || []).some((tocItem) => {
        return normalizeId(tocItem?.entityId) === itemId &&
          normalizeType(tocItem?.entityType) === itemType
      })
    },
    getHighlightedTag (match) {
      const highlightedFields = this.getHighlightedFields(match)
      if (!highlightedFields) return ''
      else {
        const keys = Object.keys(highlightedFields)
        const key = keys.find(k => k.startsWith("tag:"))
        if (key) {
          const values = highlightedFields[key]
          const tagName = key.substr(4,key.length - 4)
          return  values ? tagName + ' : ' + values[0] : ''
        } else return ''
      }
    },
    getItemMatch(item) {
      const normalizeId = (v) => (v == null ? '' : String(v))
      const normalizeType = (v) => (v == null ? '' : String(v).trim().toLowerCase())

      const itemId = normalizeId(item?.id)
      const itemType = normalizeType(item?.type)

      return (this.highlighted || []).find((tocItem) => {
        return normalizeId(tocItem?.entityId) === itemId &&
          normalizeType(tocItem?.entityType) === itemType
      })
    },
    toggle() {
      this.open = !this.open;
    },
    select(itemPath) {
      const q = this.tocQuery
      if (q) {
        this.$store.dispatch('search/setBomQuery', q)
      }
      const event = this.isWidget ? 'widgetNav': 'select'
      this.$emit(event, itemPath);
    }
  }
};
</script>

<style scoped lang="scss">

a {
  width: 100%;
}

.menu-list li ul {
  margin: 0 0 0 1rem;
  padding: 0 0 0 .1rem;
  border-left-width: .25rem;
}

.toc-menu-list:hover {
  cursor: pointer;
  background-color: hsl(0, 0%, 96%);
}

.toc-menu-list {
  border-radius: 2px;
  padding: 0.5em 0.75em;
  color: #4a4a4a;
  display: block;
  width: 100%;

  &.is-active {
    background-color: var(--primary);
    color: var(--primary--color-invert);
    border-radius: 4px;
  }
  &.is-active:hover {
    background-color: var(--primary);
  }
}

</style>
